import Constants from 'expo-constants';

import {
  AddCreditAccountDocument,
  type IUserAccountsQuery,
  UserAccountsDocument,
} from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';

import type {
  CardData,
  EncryptionResult,
  IOrbitalInputVaulting,
  IVaultService,
  IWorldPayInputVaulting,
  OrbitalEncryptionResult,
  VaultResult,
  WorldpayEncryptionResult,
} from '..';
import { PaymentProcessor, usePaymentMethodStore } from '..';
import type { Logger } from '../core/Logger';

export interface VaultServiceConfig {
  storeId: string;
  cardData: CardData;
  logger: Logger;
}

/**
 * VaultService - Handles card vaulting via backend API
 *
 * This service calls the backend's addCreditAccount mutation to vault
 * encrypted card data. It supports both Orbital and Worldpay processors.
 *
 */
export class VaultService implements IVaultService {
  private logger: Logger;
  private config: VaultServiceConfig;

  constructor(config: VaultServiceConfig) {
    this.config = config;
    this.logger = config.logger;
  }

  async vaultCard(
    encryptionResult: EncryptionResult,
    processor: PaymentProcessor
  ): Promise<VaultResult> {
    this.logger.log('Vaulting card', { processor, encryptionResult });

    if (!this.config.cardData) {
      this.logger.logError('Card data is not found');
    }

    try {
      const input = this.buildMutationInput(encryptionResult, processor);

      this.logger.log('Calling addCreditAccount mutation', {
        processor,
        hasEncryptionResult: !!encryptionResult,
        input: JSON.stringify(input, null, 2),
      });

      // No refetch in mutation: refetch would trigger setUserAccountsData with old default and cause flicker
      // Match legacy: addCreditAccountMutation({ variables: { input } })
      const response = await this.addCreditAccountMutation(
        { variables: { input } },
        { refetch: false }
      );

      const addedAccount = response?.data?.addCreditAccount;
      const accountId =
        addedAccount?.accountIdentifier ?? (addedAccount as { fdAccountId?: string } | undefined)?.fdAccountId;

      if (!accountId) {
        this.logger.logError('No account identifier returned from vault response');
      }

      this.logger.log('Vault successful', { processor, accountIdentifier: accountId });
      usePaymentMethodStore.getState().setFailedVaulting(false);

      // Set optimistic default before refetch so when hooks run setUserAccountsData we keep the new card selected
      if (accountId) {
        usePaymentMethodStore.getState().setOptimisticDefaultAccountId(accountId);
      }

      try {
        const refetchResult = await this.refetchUserAccounts();
        // Update store with refetched accounts so the new card appears in the list (match legacy payment-methods flow)
        if (refetchResult?.data) {
          usePaymentMethodStore.getState().setUserAccountsData(refetchResult.data, accountId);
        }
      } catch (refetchError) {
        this.logger.logError('Vault succeeded but refetch failed', refetchError);
      }

      return {
        accountIdentifier: accountId,
        success: true,
      };
    } catch (error) {
      this.logger.logError('Vault failed', error);
      // Don't remove card data or throw - vaulting is non-blocking
      usePaymentMethodStore.getState().setFailedVaulting(true);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown vaulting error',
      };
    }
  }

  private buildMutationInput(encryptionResult: EncryptionResult, processor: PaymentProcessor): any {
    if (!this.config.cardData) {
      this.logger.logError('Card data is not available');
    }

    const { postalCode, fullName: cardholderName, expiryMonth, expiryYear } = this.config.cardData;

    if (processor === PaymentProcessor.ORBITAL) {
      return this.buildOrbitalInputVaulting(
        encryptionResult as OrbitalEncryptionResult,
        cardholderName,
        expiryMonth,
        expiryYear,
        postalCode
      );
    } else {
      return this.buildWorldpayInputVaulting(
        encryptionResult as WorldpayEncryptionResult,
        cardholderName,
        expiryMonth,
        expiryYear,
        postalCode
      );
    }
  }

  private buildOrbitalInputVaulting(
    encryptionResult: OrbitalEncryptionResult,
    cardholderName: string,
    expiryMonth: string,
    expiryYear: string,
    postalCode: string
  ): IOrbitalInputVaulting {
    const nullFieldValue = null;
    const creditType = usePaymentMethodStore.getState().creditType;

    if (!creditType) {
      this.logger.logError('Credit type is not available');
      throw new Error('Credit type is required for vaulting');
    }

    return {
      billingAddress: {
        country: nullFieldValue,
        region: nullFieldValue,
        postalCode: postalCode,
        locality: nullFieldValue,
        streetAddress: nullFieldValue,
      },
      orbitalInput: {
        encryptedCardNum: encryptionResult.encryptedCardNum,
        encryptedCvv: encryptionResult.encryptedCvv,
        expiryMonth,
        expiryYear,
        pieFormat: encryptionResult.pieFormat,
        pieMode: encryptionResult.pieMode,
        piePhaseId: encryptionResult.piePhaseId.toString(),
        pieIntegrityCheck: encryptionResult.pieIntegrityCheck,
        pieSubscriberId: encryptionResult.pieSubscriberId,
        pieKeyID: encryptionResult.pieKeyID,
        cardBrand: creditType.toLowerCase(),
        bin: encryptionResult.bin,
      },
      fullName: cardholderName,
      storeId: this.config.storeId,
    };
  }

  /**
   * Build addCreditAccount input to match workspaces/frontend use-payment.tsx Worldpay path exactly:
   * - worldpayInput: lowValueToken, bin (from card number), last4 (from card number), cardBrand, expiryMonth, expiryYear
   * - input: billingAddress, fullName, worldpayInput, storeId (string)
   */
  private buildWorldpayInputVaulting(
    encryptionResult: WorldpayEncryptionResult,
    cardholderName: string,
    expiryMonth: string,
    expiryYear: string,
    postalCode: string
  ): IWorldPayInputVaulting {
    const nullFieldValue = null;
    const creditType = usePaymentMethodStore.getState().creditType;

    if (!creditType) {
      this.logger.logError('Credit type is not available');
      throw new Error('Credit type is required for vaulting');
    }

    const cardNumber = this.config.cardData?.cardNumber ?? '';
    // Legacy: bin/last4 from card number; expiry from form with ?? ''
    const worldpayInput = {
      lowValueToken: encryptionResult.lowValueToken,
      bin: cardNumber.substring(0, 6),
      last4: cardNumber.slice(-4),
      cardBrand: creditType.toLowerCase(),
      expiryMonth: expiryMonth ?? '',
      expiryYear: expiryYear ?? '',
    };

    // Legacy input key order: billingAddress, fullName, worldpayInput, storeId
    return {
      billingAddress: {
        country: nullFieldValue,
        region: nullFieldValue,
        postalCode: postalCode ?? '',
        locality: nullFieldValue,
        streetAddress: nullFieldValue,
        unitNumber: nullFieldValue,
      },
      fullName: cardholderName,
      worldpayInput,
      storeId: String(this.config.storeId),
    };
  }

  private async addCreditAccountMutation(
    options: { variables: { input: any } },
    refetchOptions?: { refetch: boolean }
  ): Promise<any> {
    const feAlpha3CountryCode =
      Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? 'USA' : 'CAN';

    const base = {
      mutation: AddCreditAccountDocument,
      variables: options.variables,
    };

    if (refetchOptions?.refetch !== false) {
      return await legacyClient.mutate({
        ...base,
        refetchQueries: [
          { query: UserAccountsDocument, variables: { feCountryCode: feAlpha3CountryCode } },
        ],
        awaitRefetchQueries: false,
      });
    }

    return await legacyClient.mutate(base);
  }

  private async refetchUserAccounts(): Promise<{ data?: IUserAccountsQuery } | undefined> {
    const feAlpha3CountryCode =
      Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? 'USA' : 'CAN';

    const result = await legacyClient.query({
      query: UserAccountsDocument,
      variables: { feCountryCode: feAlpha3CountryCode },
      fetchPolicy: 'network-only',
    });
    return result;
  }
}
